/**
 * 剖面数据模型 —— 纯数据结构与计算函数，不依赖任何运行时服务。
 */
import * as THREE from 'three'

/** 剖面点 */
export interface ProfilePoint {
  /** 世界坐标位置 */
  readonly position: THREE.Vector3
}

/** 剖面截面点（点云采样结果） */
export interface ProfileSectionPoint {
  /** 世界坐标位置 */
  readonly position: THREE.Vector3
  /** 点云分类码 */
  readonly classification?: number
  /** 点云强度值 */
  readonly intensity?: number
  /** RGBA 颜色，四通道取值均为 [0, 1] */
  readonly rgba?: readonly [number, number, number, number]
}

/** 剖面结果 */
export interface ProfileResult {
  /** 唯一 ID */
  readonly id: string
  /** 剖面路径点 */
  readonly points: ReadonlyArray<ProfilePoint>
  /** 剖面宽度（米） */
  width: number
  /** 剖面总长度（米） */
  readonly length: number
  /** 各段长度 */
  readonly segmentLengths: ReadonlyArray<number>
  /** 剖面截面采样点（由点云拾取填充） */
  readonly sectionPoints?: ReadonlyArray<ProfileSectionPoint>
}

import { createIdGenerator } from '../shared/idGenerator'

/** 角度转弧度常量 */
const DEG2RAD = Math.PI / 180

/** 生成唯一剖面 ID */
export const generateProfileId = createIdGenerator('profile')

/**
 * 计算相邻剖面点之间的各段长度。
 *
 * @param points - 剖面点有序列表
 * @returns 相邻两点距离数组；少于 2 个点返回空数组
 */
export function computeSegmentLengths(points: ReadonlyArray<ProfilePoint>): number[] {
  const segments: number[] = []
  for (let i = 1; i < points.length; i++) {
    segments.push(points[i - 1].position.distanceTo(points[i].position))
  }
  return segments
}

/**
 * 计算所有段长度之和。
 *
 * @param segments - 线段长度数组
 * @returns 累加总长度，单位米；空数组返回 0
 */
export function computeTotalLength(segments: ReadonlyArray<number>): number {
  let total = 0
  for (let i = 0; i < segments.length; i++) {
    total += segments[i]
  }
  return total
}

/**
 * 计算默认剖面宽度。
 *
 * 基于第一个点到相机的距离和投影半径，公式：width = 10 / projectedRadius。
 * 仅支持 PerspectiveCamera，其余情况返回默认宽度 1。
 *
 * @param points - 剖面点，至少 1 个点；空数组返回 1
 * @param camera - 当前相机，仅透视相机参与计算
 * @param viewportHeight - 视口高度，单位像素
 * @returns 默认宽度，单位米；退化情形返回 1
 */
export function computeDefaultWidth(
  points: ReadonlyArray<ProfilePoint>,
  camera: THREE.Camera,
  _viewportWidth: number,
  viewportHeight: number
): number {
  if (points.length === 0) return 1

  const distance = camera.position.distanceTo(points[0].position)
  if (distance === 0) return 1

  if (camera instanceof THREE.PerspectiveCamera) {
    const fov = camera.fov
    const projectedRadius = viewportHeight / (2 * distance * Math.tan((fov / 2) * DEG2RAD))
    if (projectedRadius === 0) return 1
    return 10 / projectedRadius
  }

  // 非透视相机返回默认宽度
  return 1
}

/**
 * 根据剖面点列表和宽度构建完整的剖面结果。
 *
 * @param points - 剖面路径点
 * @param width - 剖面宽度，单位米
 * @returns 剖面结果，含自动生成的 ID 与长度计算字段
 */
export function buildProfileResult(points: ProfilePoint[], width: number): ProfileResult {
  const segmentLengths = computeSegmentLengths(points)
  const length = computeTotalLength(segmentLengths)

  return {
    id: generateProfileId(),
    points: [...points],
    width,
    length,
    segmentLengths,
  }
}
